import NoWrapTableCell from 'Components/NoWrapTableCell'
import type { AffiliateTicketOrderLineShape } from 'Services/SalesManagement/Features/Order/orderSlice'
import { datetimeFormatter, showValueOrHyphen } from 'Util/formatters'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { Collapse, Typography } from '@mui/material'
import Box from '@mui/material/Box'
import IconButton from '@mui/material/IconButton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import { type FC, useState } from 'react'
import { t } from 'ttag'

const AffiliateOrderLineRow: FC<{
  row: AffiliateTicketOrderLineShape
  // renderRefundContent is optional since refunds for a single order line are supported only for mobile tickets and not for sales point tickets
  renderRefundContent?: () => JSX.Element
}> = ({ row, renderRefundContent }) => {
  const { ticket } = row
  const [open, setOpen] = useState(false)
  return (
    <>
      <TableRow>
        <TableCell>
          <IconButton aria-label={t`Expand row`} size="small" onClick={() => setOpen(!open)}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <NoWrapTableCell title={row.external_id}>{row.external_id}</NoWrapTableCell>
        <TableCell>{showValueOrHyphen(ticket.ticket_id)}</TableCell>
        <TableCell>{ticket.product_id}</TableCell>
        <TableCell>{ticket.product_name}</TableCell>
        <TableCell>{ticket.customer_group}</TableCell>
        <TableCell>{ticket.municipality}</TableCell>
        <TableCell>{ticket.start_zone}</TableCell>
        <TableCell>{ticket.end_zone}</TableCell>
        {renderRefundContent?.()}
      </TableRow>
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }} component="div">
                {t`Price info`}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`Price without VAT`}</TableCell>
                    <TableCell>{t`VAT`}</TableCell>
                    <TableCell align="right">{t`Amount`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell component="th" scope="row">
                      {ticket.vat_exclusive_price} €
                    </TableCell>
                    <TableCell>{ticket.vat_amount} €</TableCell>
                    <TableCell align="right">{ticket.price} €</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
              <Typography variant="h6" gutterBottom sx={{ mt: 2 }} component="div">
                {t`Additional info`}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t`External receival date`}</TableCell>
                    <TableCell>{t`External creation date`}</TableCell>
                    <TableCell>{t`External relay date`}</TableCell>
                    <TableCell>{t`Valid from`}</TableCell>
                    <TableCell>{t`Valid to`}</TableCell>
                    <TableCell>{t`Product expiration date`}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <TableRow>
                    <TableCell>{datetimeFormatter(ticket.external_receival_date)}</TableCell>
                    <TableCell>{datetimeFormatter(ticket.external_creation_date)}</TableCell>
                    <TableCell>{datetimeFormatter(ticket.external_relay_date)}</TableCell>
                    <TableCell>{datetimeFormatter(ticket.valid_from)}</TableCell>
                    <TableCell>{datetimeFormatter(ticket.valid_to)}</TableCell>
                    <TableCell>{datetimeFormatter(ticket.product_expiration_date)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  )
}

export { AffiliateOrderLineRow }
